// Cookie and Storage Utilities

// Cookie Management
export const CookieManager = {
  // Set a cookie
  setCookie: (name, value, days = 30, secure = true) => {
    let expires = "";
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = "; expires=" + date.toUTCString();
    }
    
    const secureFlag = secure && window.location.protocol === 'http:' ? '; Secure' : '';
    const sameSite = '; SameSite=Lax';
    
    document.cookie = `${name}=${value || ""}${expires}; path=/${secureFlag}${sameSite}`;
  },

  // Get a cookie
  getCookie: (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  },

  // Delete a cookie
  deleteCookie: (name) => {
    document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
  },

  // Check if cookies are enabled
  areCookiesEnabled: () => {
    try {
      document.cookie = "testcookie=1; SameSite=Lax";
      const cookiesEnabled = document.cookie.indexOf("testcookie=1") !== -1;
      document.cookie = "testcookie=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      return cookiesEnabled;
    } catch (e) {
      return false;
    }
  }
};

// Session Storage for Cart
export const CartStorage = {
  // Save cart to session storage
  saveCart: (cartItems) => {
    try {
      const cartData = {
        items: cartItems,
        timestamp: new Date().toISOString(),
        version: '1.0'
      };
      sessionStorage.setItem('seritonex_cart', JSON.stringify(cartData));
      return true;
    } catch (e) {
      console.error('Failed to save cart to session storage:', e);
      return false;
    }
  },

  // Load cart from session storage
  loadCart: () => {
    try {
      const cartData = sessionStorage.getItem('seritonex_cart');
      if (!cartData) return null;
      
      const parsed = JSON.parse(cartData);
      return parsed.items || null;
    } catch (e) {
      console.error('Failed to load cart from session storage:', e);
      return null;
    }
  },

  // Clear cart from session storage
  clearCart: () => {
    try {
      sessionStorage.removeItem('seritonex_cart');
      return true;
    } catch (e) {
      console.error('Failed to clear cart from session storage:', e);
      return false;
    }
  },

  // Get cart timestamp
  getCartTimestamp: () => {
    try {
      const cartData = sessionStorage.getItem('seritonex_cart');
      if (!cartData) return null;
      
      const parsed = JSON.parse(cartData);
      return parsed.timestamp || null;
    } catch (e) {
      return null;
    }
  }
};

// User Authentication Storage
export const AuthStorage = {
  // Save user login state
  saveUserLogin: (userData, rememberMe = false) => {
    try {
      const authData = {
        user: userData,
        timestamp: new Date().toISOString(),
        rememberMe: rememberMe
      };

      if (rememberMe) {
        // Save to cookies for persistent login (30 days)
        CookieManager.setCookie('seritonex_user_token', userData.token || userData.uid, 30);
        CookieManager.setCookie('seritonex_user_id', userData.uid, 30);
        CookieManager.setCookie('seritonex_user_email', userData.email, 30);
        CookieManager.setCookie('seritonex_remember_me', 'true', 30);
        
        // Also save to localStorage as backup
        localStorage.setItem('seritonex_auth', JSON.stringify(authData));
      } else {
        // Save to session storage only
        sessionStorage.setItem('seritonex_auth', JSON.stringify(authData));
      }
      
      return true;
    } catch (e) {
      console.error('Failed to save user login:', e);
      return false;
    }
  },

  // Load user login state
  loadUserLogin: () => {
    try {
      // First check if remember me is enabled
      const rememberMe = CookieManager.getCookie('seritonex_remember_me') === 'true';
      
      if (rememberMe) {
        // Load from cookies
        const token = CookieManager.getCookie('seritonex_user_token');
        const userId = CookieManager.getCookie('seritonex_user_id');
        const email = CookieManager.getCookie('seritonex_user_email');
        
        if (token && userId && email) {
          return {
            uid: userId,
            email: email,
            token: token,
            fromCookie: true,
            rememberMe: true
          };
        }
        
        // Fallback to localStorage
        const authData = localStorage.getItem('seritonex_auth');
        if (authData) {
          const parsed = JSON.parse(authData);
          return { ...parsed.user, fromStorage: true, rememberMe: true };
        }
      } else {
        // Load from session storage
        const authData = sessionStorage.getItem('seritonex_auth');
        if (authData) {
          const parsed = JSON.parse(authData);
          return { ...parsed.user, fromSession: true, rememberMe: false };
        }
      }
      
      return null;
    } catch (e) {
      console.error('Failed to load user login:', e);
      return null;
    }
  },

  // Clear user login state
  clearUserLogin: () => {
    try {
      // Clear cookies
      CookieManager.deleteCookie('seritonex_user_token');
      CookieManager.deleteCookie('seritonex_user_id');
      CookieManager.deleteCookie('seritonex_user_email');
      CookieManager.deleteCookie('seritonex_remember_me');
      
      // Clear storage
      localStorage.removeItem('seritonex_auth');
      sessionStorage.removeItem('seritonex_auth');
      
      return true;
    } catch (e) {
      console.error('Failed to clear user login:', e);
      return false;
    }
  },

  // Check if user should be remembered
  shouldRememberUser: () => {
    return CookieManager.getCookie('seritonex_remember_me') === 'true';
  }
};

// Privacy Preferences
export const PrivacyManager = {
  // Check if user has consented to cookies
  hasConsented: () => {
    const consent = localStorage.getItem('cookieConsent');
    return consent === 'accepted';
  },

  // Check if user has declined cookies
  hasDeclined: () => {
    const consent = localStorage.getItem('cookieConsent');
    return consent === 'declined';
  },

  // Get consent status
  getConsentStatus: () => {
    return localStorage.getItem('cookieConsent') || 'pending';
  },

  // Set consent
  setConsent: (status) => {
    localStorage.setItem('cookieConsent', status);
    
    // If declined, clear all non-essential cookies
    if (status === 'declined') {
      AuthStorage.clearUserLogin();
      // Keep only essential cookies for website functionality
    }
  },

  // Clear all privacy data
  clearAll: () => {
    localStorage.removeItem('cookieConsent');
    AuthStorage.clearUserLogin();
    CartStorage.clearCart();
  }
};

// Utility function to check browser storage support
export const StorageSupport = {
  hasLocalStorage: () => {
    try {
      const test = 'test';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  },

  hasSessionStorage: () => {
    try {
      const test = 'test';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  },

  hasCookies: () => {
    return CookieManager.areCookiesEnabled();
  }
};

export default {
  CookieManager,
  CartStorage,
  AuthStorage,
  PrivacyManager,
  StorageSupport
};